import ControlPointRoundedIcon from '@mui/icons-material/ControlPointRounded';
import DisabledByDefaultOutlinedIcon from '@mui/icons-material/DisabledByDefaultOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import React, { useState } from 'react';

import { accentButtonSx } from './SummaryBar.styles';

interface SummaryBarActionsProps {
  onStatusChange?: () => void;
  onAllocate?: () => void;
  onPartAction?: () => void;
  onTask?: () => void;
  onCancelFutile?: () => void;
  onReopen?: () => void;
  /**
   * When set, the Status button is rendered disabled with this string as a
   * tooltip explaining why. Used on closed jobs where status changes are
   * gated behind a reopen.
   */
  statusDisabledReason?: string;
}

// Disabled-state styling for the Status button on closed jobs. We keep the
// pill shape but flip to a muted grey so it reads as inactive while still
// occupying the same slot, preserving layout stability.
const disabledBtnSx = {
  py: '5px',
  px: '10px',
  textTransform: 'none' as const,
  fontWeight: 600,
  fontSize: '11px',
  whiteSpace: 'nowrap',
  '&.Mui-disabled': {
    bgcolor: 'action.disabledBackground',
    color: 'text.disabled',
  },
};

export const SummaryBarActions: React.FC<SummaryBarActionsProps> = ({
  onStatusChange,
  onAllocate,
  onPartAction,
  onTask,
  onCancelFutile,
  onReopen,
  statusDisabledReason,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const hasMenuItems = Boolean(onTask || onCancelFutile || onReopen);
  const statusDisabled = Boolean(statusDisabledReason);

  const statusButton = onStatusChange && (
    <Button
      variant="contained"
      size="small"
      onClick={statusDisabled ? undefined : onStatusChange}
      disabled={statusDisabled}
      sx={statusDisabled ? disabledBtnSx : accentButtonSx}
    >
      Status
    </Button>
  );

  return (
    <>
      {statusDisabled && statusDisabledReason ? (
        <Tooltip title={statusDisabledReason} arrow placement="bottom">
          {/* span wrapper lets the tooltip catch hover on a disabled button */}
          <span>{statusButton}</span>
        </Tooltip>
      ) : (
        statusButton
      )}
      {onAllocate && (
        <Button variant="contained" size="small" onClick={onAllocate} sx={accentButtonSx}>
          Allocate
        </Button>
      )}
      {onPartAction && (
        <Button variant="contained" size="small" onClick={onPartAction} sx={accentButtonSx}>
          Part Action
        </Button>
      )}
      {hasMenuItems && (
        <>
          <IconButton
            aria-label="More actions"
            aria-controls={menuOpen ? 'more-actions-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? 'true' : undefined}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ color: 'v4.accent', p: 0.5 }}
          >
            <ControlPointRoundedIcon />
          </IconButton>
          <Menu
            id="more-actions-menu"
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={() => setAnchorEl(null)}
            MenuListProps={{ 'aria-labelledby': 'more-actions-menu' }}
          >
            {onTask && (
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  onTask();
                }}
              >
                <ViewInArIcon sx={{ mr: 1 }} />
                Tasks
              </MenuItem>
            )}
            {onCancelFutile && (
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  onCancelFutile();
                }}
              >
                <DisabledByDefaultOutlinedIcon sx={{ mr: 1 }} />
                Cancel/Futile
              </MenuItem>
            )}
            {onReopen && (
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  onReopen();
                }}
              >
                <RestartAltIcon sx={{ mr: 1 }} />
                Reopen Job
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </>
  );
};
